import React from 'react'

import { useGetSocialSignIn } from '../../libs/authentication'
import { Container, Divider, ButtonContainer } from './SocialSignIn.styles'
import { Location } from 'history'
import { useLocation } from 'react-router'
import { ContinueWithApple } from './ContinueWithApple'
import { ContinueWithGoogle } from './ContinueWithGoogle'
import { ContinueWithSSO } from './ContinueWithSSO'
import { GetSocialAuthStartUrlMutationVariables } from '@sketch/gql-types'
import { isPrivateCloud } from '@sketch/env-config'

interface LocationState {
  from?: Location
  // In some cases, we need to force the users to login with email and password
  // for their SSO account, for example, when accepting a workspace invitation
  hideSSO?: boolean
}

interface SocialSignInProps {
  /**
   * Optional redirect destination to override the default behavior.
   * When provided, this will be used as the destinationUrl for social sign-in
   * instead of deriving it from location.state?.from
   *
   * This is used in the AcceptInvite flow to ensure the user is redirected
   * back to the invite acceptance page after signing in.
   *
   * If not provided, the user will be redirected to their original destination
   * or the default entry point after signing in.
   *
   */
  redirectTo?: string
  hideSSO?: LocationState['hideSSO']

  /**
   * Used in Campaign Sign in and Campaign Sign Up view, this parameter
   * indicates whether to create a new workspace after social sign in.
   */
  createWorkspace?: boolean
}

const getDestinationUrl = (redirectTo?: string, from?: Location | string) => {
  if (redirectTo) {
    return redirectTo
  }

  if (!from) {
    return null
  }

  // "AcceptProjectInviteByTokenView" passes a string to "from"
  // and not a location object
  if (typeof from === 'string') {
    return from
  }

  // Build URL from Location object
  const { pathname = '', search = '', hash = '' } = from

  return `${pathname}${search}${hash}`
}

export const SocialSignIn = ({
  redirectTo,
  hideSSO,
  createWorkspace,
}: SocialSignInProps) => {
  const location = useLocation<LocationState>()
  const { state } = location

  const { getSocialAuthStartUrl, loadingState } = useGetSocialSignIn()

  const showSSOSignInButton = !state?.hideSSO && !hideSSO

  const handleSocialSignIn = (
    provider: GetSocialAuthStartUrlMutationVariables['provider']
  ) => {
    const destinationUrl = getDestinationUrl(redirectTo, state?.from)

    getSocialAuthStartUrl({
      variables: {
        provider,
        destinationUrl,
        createWorkspace,
        /**
         * SIGN_ON is used both for sign in and sign up flows
         */
        action: 'SIGN_ON',
      },
    })
  }

  const isAppleLoading =
    loadingState.isLoading && loadingState.provider === 'APPLE'

  const isGoogleLoading =
    loadingState.isLoading && loadingState.provider === 'GOOGLE'

  const showSocialButtons = !isPrivateCloud
  const hasVisibleButtons = showSocialButtons || showSSOSignInButton

  return (
    <Container data-testid="social-sign-in">
      {hasVisibleButtons && (
        <ButtonContainer $isSSOEnabled={showSSOSignInButton}>
          {showSocialButtons && (
            <ContinueWithApple
              onClick={() => handleSocialSignIn('APPLE')}
              loading={isAppleLoading}
            />
          )}
          {showSocialButtons && (
            <ContinueWithGoogle
              onClick={() => handleSocialSignIn('GOOGLE')}
              loading={isGoogleLoading}
            />
          )}
          {showSSOSignInButton && <ContinueWithSSO redirectTo={redirectTo} />}
        </ButtonContainer>
      )}
      {hasVisibleButtons && <Divider>or</Divider>}
    </Container>
  )
}
